/**
 * Validate a single field value against its schema
 * Returns error string or null
 */
export const validateField = (field, value) => {
  const { label, required, validation = {}, type } = field;

  const isEmpty = value === null || value === undefined || value === '' ||
    (Array.isArray(value) && value.length === 0);

  if (required && isEmpty) {
    return `${label} is required`;
  }

  if (isEmpty) return null; // Optional field, no further validation needed

  if (type === 'email') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
  }

  if (type === 'tel') {
    const telRegex = /^[+\d\s\-()]{7,20}$/;
    if (!telRegex.test(value)) return 'Please enter a valid phone number';
  }

  if (type === 'url') {
    try { new URL(value); } catch { return 'Please enter a valid URL'; }
  }

  if (validation.minLength && String(value).length < validation.minLength) {
    return `${label} must be at least ${validation.minLength} characters`;
  }

  if (validation.maxLength && String(value).length > validation.maxLength) {
    return `${label} must be no more than ${validation.maxLength} characters`;
  }

  if (validation.min !== null && validation.min !== undefined && Number(value) < validation.min) {
    return `${label} must be at least ${validation.min}`;
  }

  if (validation.max !== null && validation.max !== undefined && Number(value) > validation.max) {
    return `${label} must be no more than ${validation.max}`;
  }

  if (validation.pattern) {
    try {
      const regex = new RegExp(validation.pattern);
      if (!regex.test(value)) {
        return validation.patternMessage || `${label} format is invalid`;
      }
    } catch (e) { /* invalid regex, skip */ }
  }

  if ((type === 'file' || type === 'image') && value instanceof File) {
    const maxBytes = (validation.maxSizeMB || 5) * 1024 * 1024;
    if (value.size > maxBytes) {
      return `File size must be under ${validation.maxSizeMB || 5}MB`;
    }
  }

  return null;
};

/**
 * Validate all fields in a step's sections
 * Returns { isValid, errors: { fieldId: errorMessage } }
 */
export const validateFieldsStep = (step, fieldData) => {
  const errors = {};
  let isValid = true;

  (step.sections || []).forEach((section) => {
    (section.fields || []).forEach((field) => {
      if (field.hidden || field.disabled) return;
      const err = validateField(field, fieldData[field.id]);
      if (err) {
        errors[field.id] = err;
        isValid = false;
      }
    });
  });

  return { isValid, errors };
};

/**
 * Validate selection groups in a step
 */
export const validateSelectionStep = (step, selectionData) => {
  const errors = {};
  let isValid = true;

  (step.selectionGroups || []).forEach((group) => {
    if (!group.required) return;
    const val = selectionData[group.id];
    const isEmpty = val === null || val === undefined ||
      (Array.isArray(val) && val.length === 0);
    if (isEmpty) {
      errors[group.id] = `Please select a ${group.title}`;
      isValid = false;
    }
  });

  return { isValid, errors };
};
